import isNumber from 'lodash/isNumber'
import { setCellType } from 'dataContainer/codes.cells'
import { AutoDropdownEditor } from 'handsontable/editors/auto-dropdown-editor'
import { DateEditor } from 'handsontable/editors/date-editor'
import { LinkEditor } from 'handsontable/editors/link-editor'
import { MultiSelectEditor } from 'handsontable/editors/multi-select-editor'
import { OpenEditor } from 'handsontable/editors/open-editor'
import { TextEditor } from 'handsontable/editors/text-editor'
import { TreeSelectEditor } from 'handsontable/editors/tree-select-editor'
import { getRenderer } from 'handsontable/renderers/renderers'
import { t } from '@lingui/core/macro'

/**
 * This provides the interface between the data container and the
 * hands on table required dependencies.  This abstraction will
 * allow us to build similar binders for other render tables if
 * we ever need to go that way.
 */
export class HandsOnTableBinder {
  constructor({ dataContainer, table, options }) {
    this.table = table
    this.dataContainer = dataContainer
    this.visibleRenderColumnsOrderById = []
    this.options = {
      setCopyStatus: () => {},
      setPasteStatus: () => {},
      setRowHeightObserver: () => {},
      featureChipsUILists: false,
      ...options,
    }
    this.templateFields = dataContainer.templateFields

    this.tableIsReadOnly = this.table.getSettings().readOnly

    this.setForceHOTRender()

    this.buildAllRenderColumns()

    this.dataContainer.allRenderColumns = this.allRenderColumnsByFieldId

    // monitor rows created through HoT.  The data container may not know
    // that these were added.  So we need to let it know.
    // this.table.addHookOnce('afterCreateRow', rowNum => {
    //   // this.dataContainer.touchConstants(rowNum);
    // })

    this.table.addHookOnce('afterRemoveRow', rowNum => {
      // when the row is deleted we need to do something.
      // this.dataContainer.touchConstants(rowNum);
      this.options.checkForInvalid()
      this.dataContainer.unitializeRow(rowNum)
    })

    this.table.addHookOnce('beforeAutofill', (selectionData, sourceRange, targetRange) => {
      const start = targetRange.from
      const end = targetRange.to
      const data = selectionData
      const templateColumns = Object.values(this.visibleRenderColumns)

      for (let { row } = start; row < end.row + 1; row++) {
        for (let { col } = start; col < end.col + 1; col++) {
          if (
            !templateColumns[col].field.is_pattern &&
            templateColumns[col].field.governance_type !== 'random'
          ) {
            this.recordChange([
              row,
              `${templateColumns[col].field_id}.cell_value`,
              '',
              data[0][col - start.col],
            ])
          }
        }
      }
    })

    this.table.addHook('beforePaste', this.beforePaste.bind(this))
    this.table.addHook('beforeCopy', () => {
      this.options.setCopyStatus(true)
    })

    this.table.addHook('setRowHeightObserver', this.options.setRowHeightObserver)

    // monitor the datacontainer for load data events
    this.dataContainer?.addHook('afterLoadData', this.afterLoadData.bind(this))

    this.dataContainer?.addHook('recordChange', this.recordChange.bind(this))

    // this beautiful method brought to you by hands on table updateSettings breaking all the metadata we synced painstankingly before.
    this.resyncMeta = () => {
      this.initChanges?.forEach(change => {
        change.forEach(appliedChange => {
          syncCellMetadata.call(this, { appliedChange })
        })
      })
      this.initChanges = null
    }
  }

  setTemplateFields(templateFields) {
    this.templateFields = templateFields
  }

  buildAllRenderColumns() {
    this.allRenderColumns = getHandsOnTableColumns({
      templateFields: this.templateFields,
      dataContainer: this.dataContainer,
      featureChipsUILists: this.options.featureChipsUILists,
    })

    this.visibleRenderColumns = this.allRenderColumns.filter(column => column.visible_on_form)
    this.allRenderColumnsByFieldId = this.allRenderColumns.reduce((memo, column) => {
      const fieldId = column.field_id || (column.field && column.field.id)
      memo[fieldId] = column
      return memo
    }, {})

    this.visibleRenderColumnsOrderById = this.visibleRenderColumns.reduce((memo, col, index) => {
      memo[col.field_id] = index
      return memo
    }, {})

    return this.allRenderColumnsByFieldId
  }

  /**
   * This is called after the data container has loaded data, and the binder has
   * updated the columns to reflect the data container's changes.
   * We need this because in some cases, the data container will change the columns
   * after the data has been loaded
   */
  handleAfterLoadChanges() {
    // If the data container doesn't have any after load change, we don't need to do anything.
    if (!this.dataContainer?.fieldsUpdatedAfterLoad?.length) return

    const getColWidth = col => (col.governance_type === 'link-validation' ? 250 : 200)

    const updatedColumns = []
    this.dataContainer.fieldsUpdatedAfterLoad.forEach(fieldId => {
      const fieldVisibility = this.dataContainer.getFieldVisibility(fieldId)
      // Currently we only care about the visible_on_form property
      this.allRenderColumnsByFieldId[fieldId].visible_on_form = fieldVisibility
      this.visibleRenderColumns.push(this.allRenderColumnsByFieldId[fieldId])
      updatedColumns.push(fieldId)
    })

    this.dataContainer.fieldsUpdatedAfterLoad = []

    // Setting the hidden columns into the correct precedence order.
    // FIXME: This breaks pasting when the order of columns has changed. Need to find a
    // solution that orders by precedence AND pasting see's the updated column position.
    // this.visibleRenderColumns = [...this.visibleRenderColumns].sort(orderByPrecedence)

    const tableSetting = this.table?.getSettings()
    /**
     * Since we manually added a new column to the table, we need to update the colWidths
     * inside the table settings to reflect the new column width.
     */
    this.table?.updateSettings({
      ...tableSetting,
      colWidths: [
        ...tableSetting.colWidths,
        ...updatedColumns.map(updatedColId =>
          getColWidth(this.allRenderColumnsByFieldId[updatedColId]),
        ),
      ],
    })
  }

  /**
   * Not called by the Handsontable afterLoadData, since we override it in updateSettings.
   */
  afterLoadData(changes) {
    if (!this.table.isDestroyed) {
      this.initChanges = changes
      changes.forEach(change => {
        // change.init = true;
        change.forEach(appliedChange => {
          syncCellMetadata.call(this, { appliedChange })
        })
      })

      this.handleAfterLoadChanges()
    }
  }

  getDataSchema() {
    // todo: should this just be implemented on the binder?
    // it's kind of related to both concepts
    return this.dataContainer.getDataSchema()
  }

  getData() {
    return this.dataContainer.data
  }

  recordChange(change, options = {}) {
    const [rowNum, columnProp, oldValue, newValue] = change
    const fieldId = getFieldIdFromColumnProp(columnProp)
    const batch = options?.batch || false

    // if we don't have a field mapping, then we don't have any data to update
    if (!fieldId) {
      return
    }

    // record the change into the data container
    const appliedChanges = this.dataContainer.recordChange(
      rowNum,
      fieldId,
      newValue,
      oldValue,
      // some of the internals still require this.  eventually we'll be rid of it
      {
        // used in link transformer to trigger re-render
        handsOnTable: this.table,
        // used in link transformer to calculate pattern values...
        rowNum,
        root: true,
        batch,
      },
    )

    // update the new value so that it doesn't use the old value
    change[3] =
      this.dataContainer.data[rowNum][fieldId] &&
      this.dataContainer.data[rowNum][fieldId].cell_value
    // sync cell metadata in hot for all updated cells

    appliedChanges.forEach(appliedChange => {
      const options = { appliedChange, readOnly: this.tableIsReadOnly }
      syncCellMetadata.call(this, options)
    })
  }

  /**
   * Fired before values are pasted into table.
   * - Trigger recordChange callback; notify of cell change.
   * - Call table re-render to make sure the updated metadata is shown.
   * @param {array} data An array of arrays which contains data to paste.
   * @param {array} coords An array of objects with ranges of the visual indexes
   * (startRow, startCol, endRow, endCol) that correspond to the previously selected area.
   */
  beforePaste(data, cords) {
    const cordinates = { ...cords[0] }
    const { startRow, startCol, endRow, endCol } = cordinates
    const pastedDataRowsLength = data.length
    const pastedDataColumnsLength = data[0].length
    const templateColumns = Object.values(this.visibleRenderColumns)
    const pastedColValues = []
    let trackColValue = null

    this.options.setPasteStatus(true)

    const rowsNeededAfterPaste = endRow + pastedDataRowsLength

    if (rowsNeededAfterPaste > 0) {
      addRows.call(this, rowsNeededAfterPaste - this.table.countRows(), endRow)
    }

    if (data.length + startRow > 3000) {
      this.options.overflowCallback && this.options.overflowCallback()
    }

    for (
      let row = startRow;
      row < (this.options.resubmit ? this.getData().length : endRow + pastedDataRowsLength);
      row++
    ) {
      const numberOfRowsSelected = endRow - startRow + 1 // 1 = include starting row
      const numberOfColumnsSelected = endCol - startCol + 1 // 1 = include starting row
      const pasteEndOnRow = pastedDataRowsLength + startRow - row
      const withinRangeOfSelection = row >= startRow && row <= endRow
      const withinRangeOfPastedLength = pastedDataRowsLength + startRow > row
      const cellValueRowIndex = pastedDataRowsLength > 1 ? pastedDataRowsLength - pasteEndOnRow : 0

      for (let col = startCol; col < startCol + pastedDataColumnsLength; col++) {
        const withinColumnRangeOfSelection = col >= startCol && col <= endCol

        // SKIP CELLS THAT FAIL THESE SCENARIOS
        if (numberOfRowsSelected === 1) {
          // SCENARIO: 1 row and 1 column is selected, outside fo the range of the selection.
          if (numberOfColumnsSelected > 1 && !withinRangeOfSelection) {
            continue
          }
          // SCENARIO: 1 row is selected; 1 or more rows of data are being pasted to the grid.
          if (pastedDataRowsLength >= 1 && !withinRangeOfPastedLength) {
            continue
          }
        }
        // SCENARIO: More than 1 row is selected
        if (numberOfRowsSelected > 1) {
          // Rows that are outside the selection
          if (!withinRangeOfSelection) {
            continue
          }

          if (!withinColumnRangeOfSelection) {
            continue
          }
        }

        const tmplCol = templateColumns[col]
        const notReadOnly = !this.dataContainer.data[0][tmplCol?.field_id]?.metadata.readOnly
        const currentDataRowValue = data[cellValueRowIndex]?.length
          ? data[cellValueRowIndex][col - startCol]
          : null
        const matchPastedValueToColumn = pastedColValues.find(d => d.col === col && d.row === row)

        // Set value to use for this cell; use the value from this column from a previous coordinate or the current cell value.
        trackColValue = matchPastedValueToColumn
          ? matchPastedValueToColumn?.value
          : currentDataRowValue

        if (!matchPastedValueToColumn) {
          // Track this column cell value and position if this coordinate wasn't already recorded.
          pastedColValues.push({
            col,
            row,
            value: trackColValue,
          })
        }

        const notPatternGovernanceType = tmplCol?.governance_type !== 'pattern'

        if (tmplCol && notReadOnly && trackColValue && notPatternGovernanceType) {
          this.recordChange([row, `${tmplCol?.field_id}.cell_value`, null, trackColValue], {
            batch: true,
          })
        }
      }
    }

    this.options.setPasteStatus(false)

    this.dataContainer.changeCallback(this.dataContainer)

    /**
     * Render the table manually, and return false;
     * to prevent a full paste of the clipboard to the table;
     * instead, control what's placed on the table using the recordChange callback;
     * render will refresh the table to display what was copied there.
     * Without this, we would not be able to limit the pasted rows to
     * just the selection coordinates.
     */
    setTimeout(() => {
      if (!this.table.isDestroyed) {
        this.table.render()
      }
    }, 200)

    return false
  }

  setForceHOTRender() {
    this.dataContainer.forceHOTRender = () => {
      if (!this.table.isDestroyed) {
        this.table.render()
      }
    }
  }
}

/**
 * Add rows to Handsontable instance if the pasted data contains more rows
 * than currently rendered/visible.
 *
 * For example, if 50 rows are visible and 75 rows of data are pasted,
 * this function will add 25 rows.
 * @param {number} rowsToAdd
 * @param {number} startRowNum
 */
function addRows(rowsToAdd, startRowNum) {
  this.table.batch(() => {
    Array.from({ length: rowsToAdd }, (_, i) => i + 1 + startRowNum).forEach(visualRowIdx =>
      this.table.alter('insert_row_above', visualRowIdx),
    )
  })
}

// todo: these should be specific by type
function syncCellMetadata({ appliedChange, readOnly }) {
  const { rowNum, fieldId, originalValue } = appliedChange
  const renderColumn = this.allRenderColumnsByFieldId[fieldId]

  if (!renderColumn) {
    return
  }

  const rowObj = this.dataContainer.data[rowNum]
  const valueObj = (rowObj || {})[fieldId]
  const colNumStr = this.table.propToCol(renderColumn.data)
  const colNum = typeof colNumStr === 'string' ? Number(colNumStr.split('.')[0]) : colNumStr

  if (!valueObj) {
    // it's a link validation column
    return
  }

  let isReadOnly = valueObj.metadata.readOnly || readOnly

  if (renderColumn.governance_type === 'link' && renderColumn.autofill) {
    isReadOnly = true
  }

  const { validOptions } = valueObj.metadata
  const cellMetaObject = {
    readOnly: isReadOnly,
  }

  if (this.dataContainer.options.resubmit) {
    cellMetaObject.originalValue = originalValue
  }

  if (validOptions) {
    cellMetaObject.source = validOptions
  }

  this.table.setCellMetaObject(rowNum, colNum, cellMetaObject)
}

// todo: I think we can clean this up once the other items are in place
function getHandsOnTableColumns({ templateFields, dataContainer, featureChipsUILists }) {
  const templateColumns = templateFields

  if (!templateColumns) {
    return []
  }

  const isLinkCol = col => {
    const isVariable = col.field && col.field.segment_type === 'variable'
    const governance = isVariable ? col : col.field
    return governance && governance.governance_type === 'link'
  }

  // find the link column if it exists
  const linkColumn = templateColumns.find(isLinkCol)
  const nonLinkColumns = templateColumns.filter(col => !isLinkCol(col))
  if (linkColumn && linkColumn.autofill) {
    linkColumn.readOnly = true
  }
  const renderableLinkColumns = getRenderableLinkColumns(linkColumn)

  // concat the link columns
  let renderColumns = nonLinkColumns.concat(renderableLinkColumns)

  // sort after the link columns are added
  renderColumns.sort((a, b) => {
    return a.precedence - b.precedence
  })
  // mimics old `setDataLocation` and `setTransformers` from hands-on-table.controller.js
  renderColumns = renderColumns.map(column => {
    return {
      ...column,
      data: column.renderOnly ? column.dataSource : `${column.field_id}.cell_value`,
      renderer: getRenderer({
        templateField: column,
        dataContainer,
        featureChipsUILists,
      }),
      columnSorting: true,
      observeChanges: true,
    }
  })

  // use the common code logic
  renderColumns = renderColumns.map(setCellType)

  renderColumns.forEach(rc => {
    if (rc.type === 'autocomplete') {
      if (rc.multi === true && !rc.hierarchy) {
        // Enable feature for showing Chips UI.
        MultiSelectEditor.setFeatureChipsUILists(featureChipsUILists)
        rc.editor = MultiSelectEditor
      } else if (rc.hierarchy) {
        TreeSelectEditor.setFeatureChipsUILists(featureChipsUILists)
        rc.editor = TreeSelectEditor
      } else {
        rc.editor = AutoDropdownEditor
      }
    }
    if (rc.type === 'date') {
      rc.editor = DateEditor
    }
    if (
      rc.governance_type === 'text' ||
      rc.field.governance_type === 'text' ||
      (!rc.governance_type && !rc.field?.governance_type)
    ) {
      if (
        rc.suggestions?.length ||
        (rc.field?.validation?.show_previous && (rc.field.previous_values || rc.previous_values)) ||
        rc.field?.suggestions?.length
      ) {
        rc.editor = OpenEditor
      } else {
        rc.editor = TextEditor
      }
    }

    if (rc.governance_type === 'link' || rc.field.governance_type === 'link') {
      rc.editor = LinkEditor
    }
    // read only fields shouldn't even have an editor available.
    if (
      rc.constant_value ||
      rc.field.constant_value ||
      rc.is_pattern ||
      rc.field.is_pattern ||
      rc.autofill ||
      rc.field.autofill ||
      rc.random ||
      rc.field.random ||
      rc.autonumber ||
      rc.field.autonumber ||
      (rc.field.date && rc.field.date.auto_populate)
    ) {
      rc.editor = null
    }
  })

  return renderColumns
}

function getRenderableLinkColumns(linkColumn) {
  const shouldIncludeLinkColumns = governanceIsEnabled(linkColumn)
  return shouldIncludeLinkColumns
    ? [
        linkColumn,
        {
          renderOnly: true,
          readOnly: true,
          governance_type: 'link-validation',
          dataSource: `${linkColumn.field_id}.last_url_audits`,
          field_id: `${linkColumn.id} - ${linkColumn.field_id}`,
          field: {
            ...linkColumn.field,
            name: t`Link Status`,
            isLinkStatus: true,
            governance_type: 'link-validation',
          },
          visible_on_form: true,
          precedence: linkColumn.precedence,
        },
      ]
    : linkColumn
      ? [linkColumn]
      : []
}

function governanceIsEnabled(linkColumn = {}) {
  const isVariable = linkColumn.field && linkColumn.field.segment_type === 'variable'
  const governanceOwner = isVariable ? linkColumn : linkColumn.field
  const governance = (governanceOwner || {}).link_governance
  return governance && governance.is_active
}

const COLUMN_PROP_FIELD_ID_CACHE = {}

function getFieldIdFromColumnProp(columnProp) {
  if (COLUMN_PROP_FIELD_ID_CACHE[columnProp]) {
    return COLUMN_PROP_FIELD_ID_CACHE[columnProp]
  }

  // handsontable returns a number when there is no column mapped
  // I assume it's colIdx
  if (isNumber(columnProp)) {
    return null
  }

  const fieldId = columnProp.split('.')[0]
  COLUMN_PROP_FIELD_ID_CACHE[columnProp] = fieldId
  return fieldId
}
